import { tx } from "../lib/i18n";
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Home as HomeIcon, Baby, CalendarCheck, Wallet, Receipt, Video, Plus, Trash2, Download, XCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useMeta } from "../lib/useMeta";
import { useAuth } from "../context/AuthContext";
import { fmtFull, fmtTime, money, fmtDate, monthLabel } from "../lib/format";
import { DashShell, Stat, Badge, Table, Section, useTab } from "../components/dash/DashKit";
import { Spinner } from "../components/site/Layout";

const canJoin = (s) => s.join_token && new Date(s.start) - Date.now() <= 10 * 60000 && new Date(s.end) > Date.now();

const JoinButton = ({ s }) => {
  const { t } = useI18n();
  if (!s.join_token) return null;
  return canJoin(s)
    ? <Link to={`/les/${s.join_token}`} className="osk-btn-primary px-4 py-2 text-sm" data-testid={`join-btn-${s.id}`}><Video className="h-4 w-4" />{t("join.btn")}</Link>
    : <button disabled className="osk-btn-primary px-4 py-2 text-sm" data-testid={`join-btn-disabled-${s.id}`}><Video className="h-4 w-4" />{t("join.btn")}</button>;
};

const Attendance = ({ a, past }) => a
  ? <span className="text-xs text-emerald-700" data-testid="attendance-info">{tx("Aanwezig")} {fmtTime(a.join_time)}{a.leave_time ? `–${fmtTime(a.leave_time)}` : ""} · {Math.round(a.minutes)} min</span>
  : past ? <span className="text-xs text-osk-red">{tx("Niet aanwezig")}</span> : null;

function Overview({ bookings, overview, lang }) {
  const upcoming = bookings.filter((b) => b.status === "paid").flatMap((b) => b.sessions.filter((s) => new Date(s.end) > Date.now() && s.status === "scheduled" && !s.cancelled_by_parent).map((s) => ({ ...s, b })))
    .sort((a, b) => a.start.localeCompare(b.start)).slice(0, 6);
  return (
    <>
      <div className="mb-8 grid gap-4 sm:grid-cols-3">
        <Stat label={tx("Kinderen")} value={overview.children} icon={Baby} testid="stat-children" />
        <Stat label={tx("Lestegoed")} value={money(overview.credits.AWG || 0, "AWG")} icon={Wallet} testid="stat-credits" />
        <Stat label={tx("Komende lessen")} value={upcoming.length} icon={CalendarCheck} testid="stat-upcoming" />
      </div>
      {overview.awaiting_payment.length > 0 && (
        <Section title={tx("Openstaande maandbetalingen")}>
          {overview.awaiting_payment.map((a) => (
            <div key={a.id} className="osk-card mb-3 flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`awaiting-${a.id}`}>
              <div><p className="font-bold">{a.lesson?.title} – {monthLabel(a.month, lang)}</p><p className="text-sm text-osk-text">{tx("Betaal vóór {d} om je plek te behouden.", { d: fmtFull(a.first_session_start, lang) })}</p></div>
              <Link to={`/boeken/${a.id}`} className="osk-btn-primary px-5 py-2 text-sm" data-testid={`pay-month-${a.id}`}>{tx("Nu betalen")} {money(a.amount, a.currency)}</Link>
            </div>
          ))}
        </Section>
      )}
      <Section title={tx("Komende lessen")} action={<Link to="/lessen" className="text-sm font-bold text-osk-blue">{tx("Les zoeken")}</Link>}>
        <div className="space-y-3">
          {upcoming.map((s) => (
            <div key={s.id + s.b.id} className="osk-card flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`upcoming-session-${s.id}`}>
              <div><p className="font-bold">{s.b.lesson.title}</p><p className="text-sm text-osk-text">{s.b.child.name} · {fmtFull(s.start, lang)} – {fmtTime(s.end)} · {s.b.teacher?.display_name}</p></div>
              <JoinButton s={s} />
            </div>
          ))}
          {!upcoming.length && <div className="osk-card p-8 text-center text-osk-muted">{tx("Nog geen geplande lessen.")} <Link to="/lessen" className="font-bold text-osk-blue">{tx("Vind een les")}</Link></div>}
        </div>
      </Section>
    </>
  );
}

function Children({ children, reload }) {
  const meta = useMeta();
  const [form, setForm] = useState({ name: "", island: "aruba", school_type: "HAVO", grade: "Klas 1" });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/parent/children", form); toast.success(tx("Kind toegevoegd")); setForm({ ...form, name: "" }); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => { await api.delete(`/parent/children/${id}`); reload(); };
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      <div className="grid content-start gap-3 sm:grid-cols-2">
        {children.map((c) => (
          <div key={c.id} className="osk-card flex items-center justify-between p-5" data-testid={`child-card-${c.id}`}>
            <div><p className="text-lg font-extrabold">{c.name}</p><p className="text-sm text-osk-text">{c.school_type} · {c.grade} · {meta?.islands?.find((i) => i.code === c.island)?.name}</p></div>
            <button onClick={() => del(c.id)} className="text-osk-muted hover:text-osk-red" data-testid={`delete-child-${c.id}`}><Trash2 className="h-5 w-5" /></button>
          </div>
        ))}
        {!children.length && <div className="osk-card p-8 text-center text-osk-muted sm:col-span-2">{tx("Voeg je eerste kind toe om lessen te boeken.")}</div>}
      </div>
      <form onSubmit={add} className="osk-card space-y-4 p-5" data-testid="add-child-form">
        <h3 className="text-lg">{tx("Kind toevoegen")}</h3>
        <input className="osk-input" placeholder={tx("Naam")} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="child-name-input" />
        <select className="osk-input" value={form.island} onChange={(e) => setForm({ ...form, island: e.target.value })} data-testid="child-island-select">
          {(meta?.islands || []).map((i) => <option key={i.code} value={i.code} disabled={!i.enabled}>{i.name}{i.enabled ? "" : ` ${tx("(binnenkort)")}`}</option>)}
        </select>
        <select className="osk-input" value={form.school_type} onChange={(e) => setForm({ ...form, school_type: e.target.value, grade: meta.grades[e.target.value][0] })} data-testid="child-school-select">
          {(meta?.school_types || []).map((s) => <option key={s}>{s}</option>)}
        </select>
        <select className="osk-input" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} data-testid="child-grade-select">
          {(meta?.grades?.[form.school_type] || []).map((g) => <option key={g}>{g}</option>)}
        </select>
        <button className="osk-btn-primary w-full" data-testid="add-child-submit-btn"><Plus className="h-4 w-4" />{tx("Toevoegen")}</button>
      </form>
    </div>
  );
}

function Bookings({ bookings, reload, lang, windowHours }) {
  const cancel = async (b, s) => {
    const early = new Date(s.start) - Date.now() > windowHours * 3600000;
    if (!window.confirm(early ? tx("Les annuleren? Je ontvangt lestegoed.") : tx("Minder dan {h} uur voor aanvang: je ontvangt GEEN lestegoed. Toch annuleren?", { h: windowHours }))) return;
    try { const { data } = await api.post(`/parent/bookings/${b.id}/cancel-session`, { session_id: s.id }); toast.success(data.credited ? tx("Geannuleerd – lestegoed ontvangen") : tx("Geannuleerd")); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const stop = async (b) => {
    if (!window.confirm(tx("Deze wekelijkse reeks stoppen? Er worden geen nieuwe maandbetalingen meer verstuurd."))) return;
    await api.post(`/parent/subscriptions/${b.lesson_id}/${b.child_id}/stop`); toast.success(tx("Reeks gestopt")); reload();
  };
  return (
    <div className="space-y-4">
      {bookings.filter((b) => b.status !== "released" || b.payment_id).map((b) => (
        <div key={b.id} className="osk-card p-5" data-testid={`booking-${b.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-lg font-extrabold">{b.lesson.title}</p>
              <p className="text-sm text-osk-text">{b.child.name} · {b.teacher?.display_name} · {b.month ? monthLabel(b.month, lang) : `${b.session_ids.length} ${tx(b.session_ids.length === 1 ? "les" : "lessen")}`} · {money(b.amount, b.currency)}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge status={b.status} />
              {["held", "awaiting_payment"].includes(b.status) && <Link to={`/boeken/${b.id}`} className="osk-btn-primary px-4 py-1.5 text-xs" data-testid={`booking-pay-${b.id}`}>{tx("Betalen")}</Link>}
              {b.type === "weekly_month" && b.status === "paid" && <button onClick={() => stop(b)} className="text-xs font-bold text-osk-muted hover:text-osk-red" data-testid={`stop-series-${b.id}`}>{tx("Reeks stoppen")}</button>}
            </div>
          </div>
          {b.status === "paid" && (
            <div className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
              {b.sessions.map((s) => {
                const past = new Date(s.end) < Date.now();
                return (
                  <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm" data-testid={`booking-session-${s.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{fmtFull(s.start, lang)} – {fmtTime(s.end)}</span>
                      {s.cancelled_by_parent ? <Badge status="cancelled" /> : s.status !== "scheduled" && <Badge status={s.status} session />}
                      {!s.cancelled_by_parent && <Attendance a={s.attendance} past={past && s.status === "held"} />}
                    </div>
                    {!s.cancelled_by_parent && s.status === "scheduled" && !past && (
                      <div className="flex items-center gap-2">
                        <JoinButton s={s} />
                        {new Date(s.start) > Date.now() && <button onClick={() => cancel(b, s)} className="flex items-center gap-1 text-xs font-bold text-osk-muted hover:text-osk-red" data-testid={`cancel-session-${s.id}`}><XCircle className="h-4 w-4" />{tx("Annuleren")}</button>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {b.materials?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {b.materials.map((m) => <button key={m.id} onClick={() => downloadFile(`/files/${m.id}`, m.filename)} className="osk-pill bg-osk-light text-osk-blue"><FileText className="mr-1 h-3.5 w-3.5" />{m.filename}</button>)}
            </div>
          )}
        </div>
      ))}
      {!bookings.length && <div className="osk-card p-8 text-center text-osk-muted">{tx("Nog geen boekingen.")}</div>}
    </div>
  );
}

function Credits({ lang }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/parent/credits").then((r) => setC(r.data)); }, []);
  if (!c) return <Spinner />;
  return (
    <>
      <div className="mb-6 grid gap-4 sm:grid-cols-3"><Stat label={tx("Beschikbaar lestegoed")} value={money(c.balance.AWG || 0, "AWG")} icon={Wallet} testid="credit-balance" /></div>
      <Table testid="credits-table" rows={c.ledger} cols={[
        { label: tx("Datum"), render: (r) => fmtDate(r.created_at) },
        { label: tx("Omschrijving"), render: (r) => tx(r.reason) },
        { label: tx("Bedrag"), render: (r) => <span className={r.amount > 0 ? "font-bold text-emerald-700" : "font-bold text-osk-red"}>{r.amount > 0 ? "+" : ""}{money(r.amount, r.currency)}</span> },
      ]} />
    </>
  );
}

function Receipts() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/parent/payments").then((r) => setRows(r.data)); }, []);
  if (!rows) return <Spinner />;
  return (
    <Table testid="receipts-table" rows={rows} empty={tx("Nog geen betalingen.")} cols={[
      { label: tx("Bonnummer"), key: "receipt_number" },
      { label: tx("Datum"), render: (r) => fmtDate(r.paid_at) },
      { label: tx("Les"), key: "lesson_title" },
      { label: tx("Betaald"), render: (r) => money(r.amount, r.currency) },
      { label: tx("Lestegoed"), render: (r) => money(r.credit_applied, r.currency) },
      { label: "", render: (r) => <button onClick={() => downloadFile(`/parent/payments/${r.id}/receipt`, `${r.receipt_number}.pdf`)} className="flex items-center gap-1 font-bold text-osk-blue" data-testid={`download-receipt-${r.id}`}><Download className="h-4 w-4" />PDF</button> },
    ]} />
  );
}

export default function ParentDashboard() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const meta = useMeta();
  const [tab, setTab] = useTab("overview");
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const [b, c, o] = await Promise.all([api.get("/parent/bookings"), api.get("/parent/children"), api.get("/parent/overview")]);
    setData({ bookings: b.data, children: c.data, overview: o.data });
  }, []);
  useEffect(() => { load(); }, [load]);
  const tabs = [["overview", tx("Overzicht"), HomeIcon], ["children", tx("Kinderen"), Baby], ["bookings", tx("Boekingen & aanwezigheid"), CalendarCheck], ["credits", tx("Lestegoed"), Wallet], ["receipts", tx("Betaalbewijzen"), Receipt]];
  return (
    <DashShell testid="parent-dashboard" title={tx("Hallo {n}", { n: user.name.split(" ")[0] })} sub={tx("Alle tijden in Aruba-tijd (AST, UTC−4).")} tabs={tabs} tab={tab} setTab={setTab}>
      {!data ? <Spinner /> : (
        <>
          {tab === "overview" && <Overview bookings={data.bookings} overview={data.overview} lang={lang} />}
          {tab === "children" && <Children children={data.children} reload={load} />}
          {tab === "bookings" && <Bookings bookings={data.bookings} reload={load} lang={lang} windowHours={meta?.cancel_window_hours ?? 24} />}
          {tab === "credits" && <Credits lang={lang} />}
          {tab === "receipts" && <Receipts />}
        </>
      )}
    </DashShell>
  );
}
